import * as IoC from 'typescript-ioc';
import { action, observable } from 'mobx';
import { alertServerError } from '../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { IReactIntlProvider } from '../../../infrastructure/react-intl-provider';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../infrastructure/sub-windows';
import { IBlueprintsDAO } from '../../../api/blueprints/blueprints.dao';
import { IOrderTemplateDTO } from '../../../api/blueprints/order.template.types';
import { OrderTemplateForm } from './order-template-form/order-template-form';
import { CREATE_ORDER_TEMPLATE_TITLE_TEXT, EDIT_ORDER_TEMPLATE_TITLE_TEXT } from './order-template-form/order-template-form.texts';

export class OrderTemplatesVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @observable
    private templates: IOrderTemplateDTO[] = [];
    @observable
    public isLoading: boolean = false;
    private readonly blueprintIdToEditPopupMap: Record<string, ISubWindowFacade> = {};

    constructor(
        private readonly accountId: number,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) { }

    public get orderTemplates(): IOrderTemplateDTO[] {
        return this.templates;
    }

    public openCreateTemplateForm(): void {
        this.subWindowService.showSubWindow({
            title: this.intlProvider.getIntl().formatMessage(CREATE_ORDER_TEMPLATE_TITLE_TEXT),
            componentToHost: OrderTemplateForm,
            componentToHostProps: {
                accountId: this.accountId,
                apiCallSuccessCallback: () => {
                    this.initialize();
                },
            },
        });
    }

    @action
    public openEditTemplate(blueprintId: string): void {
        if (!this.isEditFormHidden(blueprintId)) {
            return;
        }
        this.blueprintIdToEditPopupMap[blueprintId] = this.subWindowService.showSubWindow({
            title: this.intlProvider.getIntl().formatMessage(EDIT_ORDER_TEMPLATE_TITLE_TEXT),
            componentToHost: OrderTemplateForm,
            componentToHostProps: {
                accountId: this.accountId,
                blueprintId: blueprintId,
                apiCallSuccessCallback: () => {
                    this.initialize();
                },
            },
        });
    }

    public isEditFormHidden = (blueprintId: string): boolean => {
        const instance = this.blueprintIdToEditPopupMap[blueprintId];
        return !instance || instance.getStatus() === SubWindowStatus.Closed;
    };

    @action.bound
    public async initialize(): Promise<void> {
        this.startLoading();

        try {
            const response = await this.blueprintsDAO.getOrderTemplateList({ accountId: this.accountId });
            this.setTemplates(response);
        } catch (error) {
            OrderTemplatesVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    @action.bound
    private setTemplates(orderTemplates: IOrderTemplateDTO[]): void {
        this.templates = orderTemplates
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    @action.bound
    private startLoading(): void {
        this.isLoading = true;
    }

    @action.bound
    private stopLoading(): void {
        this.isLoading = false;
    }
}
